import React, { useState } from 'react';
import { XIcon, UserPlusIcon, RadioIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { SecondaryButton } from '../ui/SecondaryButton';
interface Worker {
  name: string;
  role: string;
  responsibility: string;
  onSite: boolean;
}
const INITIAL_WORKERS: Worker[] = [{
  name: 'Mike',
  role: 'Foreman',
  responsibility: 'Task supervision and safety coordination',
  onSite: true
}, {
  name: 'Dan',
  role: 'Crane Operator',
  responsibility: 'Operate mobile crane',
  onSite: true
}, {
  name: 'Luis',
  role: 'Rigger',
  responsibility: 'Attach lifting rigging and guide load',
  onSite: true
}, {
  name: 'Jake',
  role: 'Ironworker',
  responsibility: 'Position and bolt beam',
  onSite: true
}, {
  name: 'Carlos',
  role: 'Laborer',
  responsibility: 'Assist with tools and rigging',
  onSite: true
}];
export function CrewAssignmentScreen({
  data,
  updateData,
  onNext
}: ScreenProps) {
  const [workers, setWorkers] = useState<Worker[]>(INITIAL_WORKERS);
  const handleRemove = (name: string) => {
    const updated = workers.filter(w => w.name !== name);
    setWorkers(updated);
    updateData({
      crewSize: updated.length,
      crewRoles: updated.map(w => ({
        role: w.role,
        responsibility: w.responsibility
      }))
    });
  };
  return <div className="flex flex-col h-full" data-id="element-169">
      <div className="mb-5" data-id="element-170">
        <h2 className="text-2xl font-bold text-white mb-2" data-id="element-171">Crew Assignment</h2>
        <p className="text-slate-400 text-sm" data-id="element-172">
          Workers detected on site are automatically assigned. Remove or add
          workers as needed.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2 space-y-4" data-id="element-173">
        {/* Crew count summary */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl px-4 py-3 flex items-center justify-between" data-id="element-174">
          <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-175">
            Crew on this task
          </span>
          <span className="text-white font-bold text-lg" data-id="element-176">{workers.length}</span>
        </div>

        {/* Worker cards */}
        <div className="space-y-2.5" data-id="element-177">
          <AnimatePresence data-id="element-178">
            {workers.map((worker, index) => <motion.div key={worker.name} layout initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            x: -60,
            height: 0,
            marginBottom: 0
          }} transition={{
            delay: index * 0.05,
            duration: 0.25
          }} className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden relative" data-id="element-179">
                <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${worker.role === 'Foreman' ? 'bg-orange-500' : 'bg-slate-600'}`} data-id="element-180" />
                <div className="p-4 pl-5 flex items-center gap-3" data-id="element-181">
                  <div className="flex-1 min-w-0" data-id="element-182">
                    <div className="flex items-center gap-2 mb-0.5" data-id="element-183">
                      <h4 className="text-white font-semibold text-base" data-id="element-184">
                        {worker.name}
                      </h4>
                      <span className="text-slate-500 text-xs" data-id="element-185">·</span>
                      <span className="text-slate-400 text-sm" data-id="element-186">
                        {worker.role}
                      </span>
                    </div>
                    <p className="text-slate-500 text-xs mb-2" data-id="element-187">
                      {worker.responsibility}
                    </p>
                    <div className="flex items-center gap-1.5" data-id="element-188">
                      <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" data-id="element-189" />
                      <span className="text-green-400 text-[10px] font-bold uppercase tracking-wider" data-id="element-190">
                        On site
                      </span>
                    </div>
                  </div>
                  {worker.role !== 'Foreman' && <button onClick={() => handleRemove(worker.name)} className="p-2 rounded-lg text-slate-600 hover:text-red-400 hover:bg-red-500/10 transition-colors flex-shrink-0" data-id="element-191">
                      <XIcon className="w-4 h-4" data-id="element-192" />
                    </button>}
                </div>
              </motion.div>)}
          </AnimatePresence>
        </div>
      </div>

      <div className="pt-4 space-y-3 bg-slate-950" data-id="element-193">
        <PrimaryButton onClick={onNext} data-id="element-194">Continue</PrimaryButton>
        <SecondaryButton onClick={() => {}} icon={<UserPlusIcon className="w-5 h-5" data-id="element-196" />} data-id="element-195">
          Add Worker
        </SecondaryButton>
      </div>
    </div>;
}